// Time zone picker for a restaurant. US zones come first under their everyday
// names, then every other zone the browser knows. The value is the IANA name
// ("America/New_York"), the same string the backend's localTime.js reads, and
// '' means not set (the backend's default applies).
import React, { useMemo } from 'react'
import { Box, ListSubheader, MenuItem, OutlinedInput, Select } from '@mui/material'
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded'

export const US_ZONES = [
  { value: 'America/New_York', label: 'Eastern Time', place: 'New York, Boston' },
  { value: 'America/Chicago', label: 'Central Time', place: 'Chicago, Dallas' },
  { value: 'America/Denver', label: 'Mountain Time', place: 'Denver' },
  { value: 'America/Phoenix', label: 'Arizona', place: 'Phoenix, no daylight saving' },
  { value: 'America/Los_Angeles', label: 'Pacific Time', place: 'Los Angeles, Seattle' },
  { value: 'America/Anchorage', label: 'Alaska Time', place: 'Anchorage' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time', place: 'Honolulu' }
]
const US_VALUES = new Set(US_ZONES.map(z => z.value))

// Every zone the browser knows, minus the US ones listed above.
function otherZones() {
  try {
    return Intl.supportedValuesOf('timeZone').filter(z => !US_VALUES.has(z))
  } catch (e) {
    return []
  }
}

// "UTC-5 · 8:31 AM" for a zone at `at`, or '' when the browser can't say.
function clockIn(zone, at) {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone, hour: 'numeric', minute: '2-digit', timeZoneName: 'shortOffset'
    }).formatToParts(at)
    const offset = (parts.find(p => p.type === 'timeZoneName')?.value || '').replace(/^GMT/, 'UTC') || 'UTC'
    const time = parts.filter(p => p.type !== 'timeZoneName').map(p => p.value).join('').trim()
    return `${offset === 'UTC' ? 'UTC+0' : offset} · ${time}`
  } catch (e) {
    return ''
  }
}

const usLabel = z => `${z.label} (${z.place})`
const plainLabel = v => v.replace(/_/g, ' ')
export const timezoneLabel = v => {
  const us = US_ZONES.find(z => z.value === v)
  return us ? usLabel(us) : plainLabel(v)
}

const ITEM_SX = { display: 'flex', justifyContent: 'space-between', gap: 2 }
const CLOCK_SX = { color: 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }
const SUBHEADER_SX = { lineHeight: '36px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', bgcolor: 'background.paper' }

function zoneItem(value, label, at) {
  return (
    <MenuItem key={value} value={value} sx={ITEM_SX}>
      <span>{label}</span>
      <Box component="span" sx={CLOCK_SX}>{clockIn(value, at)}</Box>
    </MenuItem>
  )
}

export default function TimezoneSelect({ value, onChange, notSetLabel = 'Not set (platform default)', sx }) {
  // Several hundred zones: build the list once, not on every keystroke elsewhere on the page.
  const { others, items } = useMemo(() => {
    const at = new Date()
    const rest = otherZones()
    return {
      others: new Set(rest),
      items: [
        <ListSubheader key="h-us" sx={SUBHEADER_SX}>United States</ListSubheader>,
        ...US_ZONES.map(z => zoneItem(z.value, usLabel(z), at)),
        ...(rest.length ? [<ListSubheader key="h-all" sx={SUBHEADER_SX}>All time zones</ListSubheader>] : []),
        ...rest.map(z => zoneItem(z, plainLabel(z), at))
      ]
    }
  }, [])
  // A saved zone this browser doesn't list still shows, so saving never drops it.
  const unlisted = value && !US_VALUES.has(value) && !others.has(value)

  return (
    <Select
      fullWidth
      size="small"
      displayEmpty
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      input={<OutlinedInput />}
      IconComponent={KeyboardArrowDownRoundedIcon}
      renderValue={v => (v ? timezoneLabel(v) : <Box component="span" sx={{ color: 'text.secondary' }}>{notSetLabel}</Box>)}
      MenuProps={{ PaperProps: { style: { maxHeight: 380 } } }}
      inputProps={{ 'aria-label': 'Timezone' }}
      sx={sx}
    >
      <MenuItem value="">{notSetLabel}</MenuItem>
      {unlisted && <MenuItem value={value}>{value}</MenuItem>}
      {items}
    </Select>
  )
}
